import React, { useState } from 'react';

// Logo URLs from brand assets - Using new CDN
const LOGO_URLS = {
  // Official logo complete (D bleu avec pin rouge)
  full: 'https://customer-assets-cm19k8pv.emergentagent.net/job_dockhta-health/artifacts/qc6dq1bv_dockhta%20logo%20complet.jpg',
  // Logo long (texte Dockhta avec slogan)
  long: 'https://customer-assets-cm19k8pv.emergentagent.net/job_dockhta-health/artifacts/ogdayog7_logo%20long%20Dockhta.jpg',
};

// SVG Logo Icon Component - Fallback when images don't load
const LogoIconSVG = ({ size = 'md', className = '', isWhite = false }) => {
  const sizes = {
    xs: 'w-8 h-8',
    sm: 'w-10 h-10',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
    xl: 'w-20 h-20',
  };
  
  return (
    <div className={`${sizes[size] || sizes.md} ${className} flex items-center justify-center`}>
      <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        {/* Blue D shape */}
        <path 
          d="M20 10 L20 90 L50 90 C75 90 90 70 90 50 C90 30 75 10 50 10 Z"
          fill={isWhite ? '#FFFFFF' : '#1976D2'}
        />
        {/* Inner white/transparent area */}
        <path 
          d="M30 20 L30 80 L50 80 C65 80 78 65 78 50 C78 35 65 20 50 20 Z"
          fill={isWhite ? '#1976D2' : '#FFFFFF'}
        />
        {/* Red location pin with cross */}
        <g transform="translate(35, 25)">
          <path 
            d="M15 0 C6.7 0 0 6.7 0 15 C0 26.25 15 45 15 45 C15 45 30 26.25 30 15 C30 6.7 23.3 0 15 0 Z"
            fill="#E53935"
          />
          {/* White cross inside pin */}
          <path 
            d="M12 10 L18 10 L18 13 L21 13 L21 19 L18 19 L18 22 L12 22 L12 19 L9 19 L9 13 L12 13 Z"
            fill="#FFFFFF"
          />
        </g>
      </svg>
    </div>
  );
};

// Logo Icon using image with SVG fallback
const LogoIcon = ({ size = 'md', className = '', isWhite = false }) => {
  // Always use SVG for icon-only display (more reliable and crisp)
  return <LogoIconSVG size={size} className={className} isWhite={isWhite} />;
};

const Logo = ({ variant = 'default', size = 'md', showText = true, showSlogan = false, className = '', textColor = 'default' }) => {
  const [imageError, setImageError] = useState(false);
  
  const sizes = {
    xs: { icon: 'w-8 h-8', img: 'h-8', text: 'text-lg', slogan: 'text-xs' },
    sm: { icon: 'w-10 h-10', img: 'h-10', text: 'text-xl', slogan: 'text-xs' },
    md: { icon: 'w-12 h-12', img: 'h-12', text: 'text-2xl', slogan: 'text-sm' },
    lg: { icon: 'w-16 h-16', img: 'h-16', text: 'text-3xl', slogan: 'text-base' },
    xl: { icon: 'w-20 h-20', img: 'h-20', text: 'text-4xl', slogan: 'text-lg' },
  };

  const sizeConfig = sizes[size] || sizes.md;
  const isWhite = variant === 'white' || textColor === 'white';
  
  // Text color based on variant
  const getTextColor = () => {
    if (isWhite) return '#FFFFFF';
    return '#1A237E';
  };
  
  const getSloganColor = () => {
    if (isWhite) return 'text-slate-300';
    return 'text-slate-500';
  };

  // Variant: full - uses the complete official logo image
  if (variant === 'full') {
    if (imageError) {
      // Fallback to SVG + text
      return (
        <div className={`flex items-center gap-2 ${className}`} data-testid="logo-full">
          <LogoIconSVG size={size} isWhite={isWhite} />
          <div className="flex flex-col">
            <span
              className={`${sizeConfig.text} font-bold tracking-tight`}
              style={{ fontFamily: 'Outfit, sans-serif', color: getTextColor() }}
            >
              Dockhta
            </span>
            <span 
              className={`${sizeConfig.slogan} ${getSloganColor()} -mt-1`}
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              La sante pres de vous
            </span>
          </div>
        </div>
      );
    }
    
    return (
      <img
        src={LOGO_URLS.full}
        alt="Dockhta - La sante pres de vous"
        className={`${sizeConfig.img} object-contain ${className}`}
        data-testid="logo-full"
        onError={() => setImageError(true)}
      />
    );
  }

  // Variant: long - text logo with slogan image
  if (variant === 'long') {
    if (imageError || isWhite) {
      // Fallback to text
      return (
        <div className={`flex flex-col ${className}`} data-testid="logo-long">
          <span
            className={`${sizeConfig.text} font-bold tracking-tight`}
            style={{ fontFamily: 'Outfit, sans-serif', color: getTextColor() }}
          >
            Dockhta
          </span>
          <span 
            className={`${sizeConfig.slogan} ${getSloganColor()} -mt-1`}
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            La sante pres de vous
          </span>
        </div>
      );
    }
    
    return (
      <img
        src={LOGO_URLS.long}
        alt="Dockhta - La sante pres de vous"
        className={`${sizeConfig.img} object-contain ${className}`}
        data-testid="logo-long"
        onError={() => setImageError(true)}
      />
    );
  }

  // Variant: icon - just the logo icon
  if (variant === 'icon') {
    return <LogoIcon size={size} className={className} isWhite={isWhite} />;
  }

  // Default variant: icon + text (for header etc)
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="logo-default">
      <LogoIcon size={size} isWhite={isWhite} />
      {showText && (
        <div className="flex flex-col">
          <span
            className={`${sizeConfig.text} font-bold tracking-tight`}
            style={{ fontFamily: 'Outfit, sans-serif', color: getTextColor() }}
          >
            Dockhta
          </span>
          {showSlogan && (
            <span 
              className={`${sizeConfig.slogan} ${getSloganColor()} -mt-1`}
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              La sante pres de vous
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Export LogoIcon for use in other components (like AIChatBubble)
export { LogoIcon, LogoIconSVG };

// Export logo URLs for backward compatibility
export const DOCKHTA_LOGOS = LOGO_URLS;

export default Logo;
